import { Track } from '#app/contexts/AnalyticsContext/types.js';
import { useAnalytics } from '#app/hooks/useAnalytics.js';
import { generateId } from '#app/utilities/generateId.js';
import { ToastAction } from '../actions.js';
import * as actionTypes from '../actionTypes.js';
import { DEFAULT_STATE, DEFAULT_TIMEOUTS } from '../constants.js';
import { ToastContext } from '../contexts/ToastContext.js';
import { reducer } from '../reducer.js';
import {
  type Message,
  type Toast,
  type ToastHandler,
  type ToastOptions,
  type ToastType,
  type ToastUtility,
} from '../types.js';
import { isValue, resolveValueOrFunction } from '../utils.js';
import { Toaster } from './Toaster.js';
import { Dispatch, type ReactNode, useMemo, useReducer } from 'react';

const createToast = (
  message: Message,
  type: ToastType = 'info',
  options?: ToastOptions,
): Toast => ({
  ariaLive: 'polite',
  autoDismiss: true,
  createdAt: Date.now(),
  dismissOnRouteChange: false,
  duration: options?.duration ?? DEFAULT_TIMEOUTS[type],
  id: options?.id ?? generateId(),
  message,
  pauseDuration: 0,
  role: 'status',
  type,
  visible: true,
  ...options,
});

const createToastHandler = ({
  dispatch,
  track,
}: {
  dispatch: Dispatch<ToastAction>;
  track: Track;
}) => {
  const createHandler =
    (type?: ToastType, uniqueTypeOptions?: ToastOptions): ToastHandler =>
    (message, options) => {
      const tst = createToast(message, type, {
        ...options,
        ...uniqueTypeOptions,
      });

      dispatch({ payload: { toast: tst }, type: actionTypes.UPSERT_TOAST });

      // Only tracking toast errors if message is not a function/component
      if (type === 'error' && isValue(message)) {
        track('toast', {
          message: String(message),
          toast_type: 'error',
        });
      }

      return tst.id;
    };

  const toastUtil: ToastUtility = (message: Message, options?: ToastOptions) =>
    createHandler('info')(message, options);

  toastUtil.error = createHandler('error');
  toastUtil.loading = createHandler('loading');
  toastUtil.success = createHandler('success');
  toastUtil.warning = createHandler('warning');
  toastUtil.content = createHandler('content', {
    autoDismiss: false,
    hideIcon: true,
  });

  toastUtil.dismiss = (toastId) => {
    dispatch({
      payload: toastId
        ? {
            toastId,
          }
        : undefined,
      type: actionTypes.DISMISS_TOAST,
    });
    setTimeout(() => {
      dispatch({
        payload: toastId
          ? {
              toastId,
            }
          : undefined,
        type: actionTypes.REMOVE_TOAST,
      });
    }, 1_000);
  };

  toastUtil.remove = (toastId) => {
    dispatch({
      payload: toastId
        ? {
            toastId,
          }
        : undefined,
      type: actionTypes.REMOVE_TOAST,
    });
  };

  toastUtil.promise = (promise, msgs, options) => {
    const id = toastUtil.loading(msgs.loading, {
      ...options,
      ...options?.loading,
    });

    promise
      .then((value) => {
        toastUtil.success(resolveValueOrFunction(msgs.success, value), {
          id,
          ...options,
          ...options?.success,
        });

        return value;
      })
      .catch((error) => {
        toastUtil.error(resolveValueOrFunction(msgs.error, error), {
          id,
          ...options,
          ...options?.error,
        });
      });

    return promise;
  };

  return Object.freeze(toastUtil);
};

export const ToastProvider = ({
  children,
}: {
  readonly children: ReactNode;
}) => {
  const [state, dispatch] = useReducer(reducer, { ...DEFAULT_STATE });
  const { track } = useAnalytics();

  const toast = useMemo<ToastUtility>(() => {
    return createToastHandler({
      dispatch,
      track,
    });
  }, [dispatch, track]);

  const context = useMemo(() => {
    return { dispatch, state, toast };
  }, [dispatch, state, toast]);

  return (
    <ToastContext.Provider value={context}>
      {children}
      <Toaster />
    </ToastContext.Provider>
  );
};
